import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  MenuItem,
  Typography,
} from "@mui/material";
import { useTranslation } from "i18n-fe";
import { END_RIDE_OUTCOMES, offeredEndRideOutcomes } from "../util/endRide";

// Card #2798 -- the "End Ride" menu section and its one-way-trip
// confirm dialog. Static i18n keys per outcome (no dynamic key building).
const OUTCOMES = {
  [END_RIDE_OUTCOMES.SCRATCH]: {
    menuTestId: "end-ride-scratch",
    labelKey: "scoring.endRideScratch",
    bodyKey: "scoring.endRideScratchBody",
  },
  [END_RIDE_OUTCOMES.NO_SHOW]: {
    menuTestId: "end-ride-no-show",
    labelKey: "scoring.endRideNoShow",
    bodyKey: "scoring.endRideNoShowBody",
  },
  [END_RIDE_OUTCOMES.WITHDRAWN]: {
    menuTestId: "end-ride-withdrawn",
    labelKey: "scoring.endRideWithdrawn",
    bodyKey: "scoring.endRideWithdrawnBody",
  },
  [END_RIDE_OUTCOMES.EXCUSED]: {
    menuTestId: "end-ride-excused",
    labelKey: "scoring.endRideExcused",
    bodyKey: "scoring.endRideExcusedBody",
  },
};

export function useEndRideOutcomeLabel() {
  const { t } = useTranslation("score");
  return (outcome) => (OUTCOMES[outcome] ? t(OUTCOMES[outcome].labelKey) : "");
}

/**
 * The "End Ride" section of the scribe's Utility menu (it left the gavel menu
 * in #3609): Scratch and No Show at every seat, Withdrawn and Excused only at
 * C. Must be a child of a MUI Menu/MenuList.
 *
 * `noShowBlocked`: the ride already has a mark, so No Show is greyed out and a
 * reason line under it says why and what to use instead (the server refuses it
 * too, ride_has_marks). The reason line is a sibling of the item, not inside
 * it, so the disabled item's dimming never fades the words.
 */
export function EndRideMenuItems({ judgePosition, onPick, noShowBlocked = false }) {
  const { t } = useTranslation("score");
  const canRule = offeredEndRideOutcomes(judgePosition).includes(END_RIDE_OUTCOMES.WITHDRAWN);
  return (
    <>
      <Typography
        data-testid="end-ride-heading"
        variant="overline"
        sx={{ px: 2, py: 0.5, display: "block", color: "text.secondary" }}
      >
        {t("scoring.endRideHeader")}
      </Typography>
      {offeredEndRideOutcomes(judgePosition).map((outcome) => {
        const blocked = noShowBlocked && outcome === END_RIDE_OUTCOMES.NO_SHOW;
        return [
          <MenuItem
            key={outcome}
            data-testid={OUTCOMES[outcome].menuTestId}
            disabled={blocked}
            aria-describedby={blocked ? "end-ride-no-show-blocked-reason" : undefined}
            // Belt and braces: MUI's pointer-events:none stops a tap, this stops
            // any other way of activating the greyed item from picking it.
            onClick={blocked ? undefined : () => onPick(outcome)}
          >
            {t(OUTCOMES[outcome].labelKey)}
          </MenuItem>,
          blocked && (
            <Typography
              key={`${outcome}-blocked`}
              id="end-ride-no-show-blocked-reason"
              data-testid="end-ride-no-show-blocked-reason"
              variant="caption"
              sx={{ px: 2, pb: 0.75, display: "block", maxWidth: 280, color: "text.secondary", whiteSpace: "normal" }}
            >
              {canRule ? t("scoring.endRideNoShowBlocked") : t("scoring.endRideNoShowBlockedNotC")}
            </Typography>
          ),
        ];
      })}
    </>
  );
}

/**
 * The "In-Ride Alerts" section of the Utility menu, over a divider with the
 * End Ride section after it. `items` are UTILITY_EVENT_TYPE_LIST entries.
 */
export function InRideAlertsMenuItems({ items, onRaise }) {
  const { t } = useTranslation("score");
  return (
    <>
      <Typography
        data-testid="utility-in-ride-section-heading"
        variant="overline"
        sx={{ px: 2, py: 0.5, display: "block", color: "text.secondary" }}
      >
        {t("scoring.inRideAlertsHeader")}
      </Typography>
      {items.map((item) => (
        <MenuItem key={item.key} onClick={() => onRaise(item.key)}>
          {t(item.menuLabelKey)}
        </MenuItem>
      ))}
    </>
  );
}

/**
 * Confirm dialog for every End Ride outcome. States the outcome, says plainly
 * it is a one way trip, and writes nothing until Confirm. Cancel (and a
 * backdrop click) is live until the write is in flight, after which the
 * request is already on the wire and backing out would be a lie.
 */
export function EndRideConfirmDialog({ outcome, onClose, onConfirm, busy, error }) {
  const { t } = useTranslation("score");
  const cfg = outcome ? OUTCOMES[outcome] : null;
  const label = cfg ? t(cfg.labelKey) : "";
  return (
    <Dialog
      open={Boolean(cfg)}
      onClose={busy ? undefined : onClose}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle data-testid="end-ride-confirm-title">
        {t("scoring.endRideConfirmTitle", { outcome: label })}
      </DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 1.5 }}>
          {cfg ? t(cfg.bodyKey) : ""}
        </DialogContentText>
        <DialogContentText
          data-testid="end-ride-one-way-notice"
          sx={{ mb: 2, fontWeight: 700, color: "text.primary" }}
        >
          {t("scoring.endRideOneWay")}
        </DialogContentText>
        {error && (
          <Alert severity="error" data-testid="end-ride-error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          color="inherit"
          disabled={busy}
          onClick={onClose}
          data-testid="end-ride-cancel-button"
        >
          {t("scoring.endRideCancel")}
        </Button>
        <Button
          variant="contained"
          color="error"
          loading={busy}
          onClick={onConfirm}
          data-testid="end-ride-confirm-button"
        >
          {t("scoring.endRideConfirm")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
